import React from "react";
import {
  MODULE_STRUCTURE,
  MODULE_DISPLAY_NAMES,
  hasModuleAccess,
} from "../../lib/modules";

/**
 * Displays enabled modules as compact badges.
 * Used in AdminTenantsTable and TenantManagement tenant list.
 *
 * - enabledModules=[] → shows <span>Brak</span>
 * - wildcard (e.g. grants:*) → shows "Dotacje (wszystkie) *"
 * - all modules in a category → shows category label
 * - partial → shows individual module display names
 */
export const ModuleDisplay: React.FC<{ enabledModules: string[] }> = ({
  enabledModules,
}) => {
  // Defensive: ensure always an array (backend may return null for legacy rows)
  const safeEnabledModules = enabledModules || [];

  const getModuleDisplayInfo = (): Array<{
    name: string;
    type: "category" | "wildcard" | "specific";
    enabled: boolean;
  }> => {
    const moduleInfo: Array<{
      name: string;
      type: "category" | "wildcard" | "specific";
      enabled: boolean;
    }> = [];

    Object.entries(MODULE_STRUCTURE).forEach(([, category]) => {
      const wildcardModule = category.wildcard;
      const categoryModules = category.modules;

      // Wildcard check
      if (wildcardModule && safeEnabledModules.includes(wildcardModule)) {
        moduleInfo.push({
          name: `${category.label} (wszystkie)`,
          type: "wildcard",
          enabled: true,
        });
      } else {
        const enabledInCategory = categoryModules.filter((mod) =>
          hasModuleAccess(safeEnabledModules, mod)
        );

        if (enabledInCategory.length > 0) {
          if (
            enabledInCategory.length === categoryModules.length &&
            categoryModules.length > 1
          ) {
            moduleInfo.push({
              name: category.label,
              type: "category",
              enabled: true,
            });
          } else {
            enabledInCategory.forEach((mod) => {
              const displayName = MODULE_DISPLAY_NAMES[mod] || mod;
              moduleInfo.push({
                name: displayName,
                type: "specific",
                enabled: true,
              });
            });
          }
        }
      }
    });

    return moduleInfo;
  };

  const moduleInfo = getModuleDisplayInfo();

  return (
    <div className="flex flex-wrap gap-0.5">
      {moduleInfo.map((module, index) => (
        <span
          key={index}
          className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs ${
            module.enabled
              ? "bg-green-100 text-green-800"
              : "bg-neutral-100 text-neutral-500"
          }`}
        >
          {module.name}
        </span>
      ))}
      {moduleInfo.length === 0 && (
        <span className="text-xs text-neutral-500">Brak</span>
      )}
    </div>
  );
};

export default ModuleDisplay;
